'use client';

import React from 'react';

import {
  Box,
  Alert,
  Stack,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';

/**
 * Unified Error Boundary Component
 * Handles:
 * 1. React component errors (via componentDidCatch)
 * 2. Unhandled promise rejections
 * 3. Global window errors
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: null, // 'react', 'promise', or 'window'
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidMount() {
    // Handle unhandled promise rejections
    this.handlePromiseRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      event.preventDefault();

      const errorMessage = event.reason?.message || event.reason || 'Unhandled promise rejection';

      this.setState({
        hasError: true,
        error: {
          message: errorMessage,
          stack: event.reason?.stack,
        },
        errorType: 'promise',
      });
    };

    // Handle global window errors
    this.handleWindowError = (event) => {
      console.error('Global window error:', event.error);

      const message = event.error?.message || event.message;

      // Handle specific error types that need user intervention
      const shouldHandle =
        message?.includes("can't access property") ||
        message?.includes('is undefined') ||
        message?.includes('is not defined') ||
        message?.includes('null is not an object') ||
        message?.includes('Cannot read properties of');

      if (shouldHandle) {
        event.preventDefault();

        this.setState({
          hasError: true,
          error: {
            message: event.error?.message || event.message || 'An unexpected error occurred',
            stack: event.error?.stack,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
          errorType: 'window',
        });
      }
    };

    window.addEventListener('unhandledrejection', this.handlePromiseRejection);
    window.addEventListener('error', this.handleWindowError);
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught a React error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
      errorType: 'react',
    });
  }

  componentWillUnmount() {
    if (this.handlePromiseRejection) {
      window.removeEventListener('unhandledrejection', this.handlePromiseRejection);
    }
    if (this.handleWindowError) {
      window.removeEventListener('error', this.handleWindowError);
    }
  }

  handleClose = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: null,
      showDetails: false,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  handleRefresh = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
  };

  getErrorTypeLabel = () => {
    const { errorType } = this.state;
    switch (errorType) {
      case 'react':
        return 'Component Error';
      case 'promise':
        return 'Promise Rejection';
      case 'window':
        return 'Runtime Error';
      default:
        return 'Error';
    }
  };

  render() {
    const { hasError, error, errorInfo, showDetails } = this.state;

    if (hasError) {
      return (
        <Dialog
          open={hasError}
          onClose={this.handleClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: 24,
            },
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Iconify
                icon="mdi:alert-circle-outline"
                width={32}
                height={32}
                sx={{ color: 'error.main' }}
              />
              <Typography variant="h5" component="div">
                Application Error
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2}>
              <Alert severity="error" sx={{ borderRadius: 1 }}>
                <Typography variant="body2">
                  The application encountered an unexpected error. You can try closing this dialog
                  to continue, or refresh the page to resolve the issue.
                </Typography>
              </Alert>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Error Type: {this.getErrorTypeLabel()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.neutral',
                    borderRadius: 1,
                    wordBreak: 'break-word',
                  }}
                >
                  {error?.message || 'Unknown error occurred'}
                </Typography>
              </Box>

              {(error?.stack || errorInfo?.componentStack) && (
                <Box>
                  <Button
                    size="small"
                    onClick={this.toggleDetails}
                    startIcon={
                      <Iconify icon={showDetails ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
                    }
                    sx={{ mb: 1 }}
                  >
                    {showDetails ? 'Hide' : 'Show'} Technical Details
                  </Button>

                  {showDetails && (
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'background.neutral',
                        borderRadius: 1,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      <Typography
                        variant="caption"
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          margin: 0,
                        }}
                      >
                        {error?.stack || errorInfo?.componentStack}
                        {error?.filename && `\n\nFile: ${error.filename}`}
                        {error?.lineno && `\nLine: ${error.lineno}`}
                        {error?.colno && `\nColumn: ${error.colno}`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Alert severity="info" sx={{ borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Tip:</strong> If this error persists after refreshing, try clearing your
                  browser cache or contact support.
                </Typography>
              </Alert>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={this.handleClose} variant="outlined" color="inherit">
              Close & Continue
            </Button>
            <Button onClick={this.handleRefresh} variant="contained" color="primary" autoFocus>
              Refresh Page
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    const { children } = this.props;
    return children;
  }
}
